import {
  BuyerNotification,
  ListResult,
  NotificationPayload,
  NotificationQuery,
  NotificationType,
} from '../types';
import { USE_MOCK_NOTIFICATIONS } from '../constants/buyer-portal';
import { notificationMock } from '../mocks/notificationMock';
import { clinicHttp } from './http';
import { ENDPOINTS } from './endpoints';

export type NotificationService = {
  list: (query?: NotificationQuery) => Promise<ListResult<BuyerNotification>>;
  unreadCount: () => Promise<number>;
  markRead: (id: string) => Promise<BuyerNotification>;
  markAllRead: () => Promise<void>;
};

type NotificationDtoBackend = {
  id: string;
  type: NotificationType;
  payload?: string | null;
  readAt?: string | null;
  createdAt: string;
};

type BackendListResult = {
  items: NotificationDtoBackend[] | null;
  skip: number;
  take: number;
  total: number;
};

const parsePayload = (raw?: string | null): NotificationPayload | undefined => {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as NotificationPayload)
      : undefined;
  } catch {
    return undefined;
  }
};

const mapNotification = (n: NotificationDtoBackend): BuyerNotification => ({
  id: n.id,
  type: n.type,
  payload: parsePayload(n.payload),
  readAt: n.readAt ?? undefined,
  createdAt: n.createdAt,
});

const realService: NotificationService = {
  list: async (query = {}) => {
    const res = await clinicHttp.get<BackendListResult>(ENDPOINTS.NOTIFICATIONS.LIST, {
      params: query,
    });
    return {
      items: (res.data.items ?? []).map(mapNotification),
      skip: res.data.skip,
      take: res.data.take,
      total: res.data.total,
    };
  },
  unreadCount: async () => {
    const res = await clinicHttp.get<number>(ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT);
    return res.data;
  },
  markRead: async (id) => {
    const res = await clinicHttp.post<NotificationDtoBackend>(
      ENDPOINTS.NOTIFICATIONS.READ(id),
    );
    return mapNotification(res.data);
  },
  markAllRead: async () => {
    await clinicHttp.post(ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ);
  },
};

export const notificationService: NotificationService = USE_MOCK_NOTIFICATIONS
  ? notificationMock
  : realService;
